import React, { useState } from "react";

import { toast } from "react-toastify";
import {
	Sparkles,
	Wand2,
	Languages,
	FileText,
	Gauge,
	Copy,
	Loader2,
	X,
} from "lucide-react";

import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { cn } from "../../lib/utils";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const HUMOR = {
	satisfeito: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
	neutro: "bg-slate-500/10 text-slate-600 dark:text-slate-400",
	irritado: "bg-destructive/10 text-destructive",
};

const RISCO = {
	alto: "bg-destructive/10 text-destructive",
	medio: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
	baixo: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
};

// Painel lateral que acompanha a conversa. Nunca envia nada sozinho: o
// atendente escolhe o que usar.
const AiCopilot = ({ ticketId, onUsarTexto }) => {
	const [aberto, setAberto] = useState(false);
	const [carregando, setCarregando] = useState(null);
	const [sugestoes, setSugestoes] = useState([]);
	const [analise, setAnalise] = useState(null);
	const [resumo, setResumo] = useState(null);
	const [score, setScore] = useState(null);

	const chamar = async (acao, rota, aplicar) => {
		setCarregando(acao);
		try {
			const { data } = await api.post(rota);
			aplicar(data);
		} catch (err) {
			toastError(err);
		}
		setCarregando(null);
	};

	const usar = (texto) => {
		onUsarTexto?.(texto);
		setAberto(false);
		toast.success("Texto colocado na caixa de mensagem.");
	};

	const copiar = async (texto) => {
		try {
			await navigator.clipboard.writeText(texto);
			toast.success("Copiado.");
		} catch (err) {
			toast.error("Não foi possível copiar.");
		}
	};

	const Bloco = ({ titulo, children }) => (
		<section className="rounded-xl border bg-card p-3">
			<h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
				{titulo}
			</h3>
			{children}
		</section>
	);

	return (
		<>
			<Button
				variant="ghost"
				size="icon"
				aria-label="Abrir copiloto de IA"
				title="Copiloto de IA"
				onClick={() => setAberto(true)}
			>
				<Sparkles className="h-5 w-5" />
			</Button>

			<Sheet open={aberto} onOpenChange={setAberto}>
				<SheetContent
					side="right"
					className="flex w-full flex-col gap-0 p-0 sm:max-w-md"
				>
					<SheetHeader className="border-b p-4">
						<SheetTitle className="flex items-center gap-2 text-base">
							<Sparkles className="h-4 w-4 text-primary" />
							Copiloto
						</SheetTitle>
						<p className="text-xs text-muted-foreground">
							A IA lê a conversa e sugere. Nada é enviado sem você confirmar.
						</p>
					</SheetHeader>

					<div className="flex-1 space-y-3 overflow-y-auto p-4">
						{/* ações */}
						<div className="grid grid-cols-2 gap-2">
							{[
								{
									acao: "sugerir",
									rotulo: "Sugerir resposta",
									icone: Wand2,
									rota: `/tickets/${ticketId}/ai/suggest`,
									aplicar: (d) => setSugestoes(d.sugestoes || []),
								},
								{
									acao: "analisar",
									rotulo: "Analisar conversa",
									icone: Gauge,
									rota: `/tickets/${ticketId}/ai/analyze`,
									aplicar: setAnalise,
								},
								{
									acao: "resumir",
									rotulo: "Resumir",
									icone: FileText,
									rota: `/tickets/${ticketId}/ai/summary`,
									aplicar: setResumo,
								},
								{
									acao: "avaliar",
									rotulo: "Avaliar atendimento",
									icone: Gauge,
									rota: `/tickets/${ticketId}/ai/score`,
									aplicar: setScore,
								},
							].map((item) => (
								<Button
									key={item.acao}
									variant="outline"
									size="sm"
									className="h-auto flex-col gap-1 py-2.5"
									disabled={Boolean(carregando)}
									onClick={() => chamar(item.acao, item.rota, item.aplicar)}
								>
									{carregando === item.acao ? (
										<Loader2 className="h-4 w-4 animate-spin" />
									) : (
										<item.icone className="h-4 w-4" />
									)}
									<span className="text-xs">{item.rotulo}</span>
								</Button>
							))}
						</div>

						{/* sugestões de resposta */}
						{sugestoes.length > 0 && (
							<Bloco titulo="Respostas sugeridas">
								<ul className="space-y-2">
									{sugestoes.map((texto, indice) => (
										<li
											key={indice}
											className="rounded-lg border bg-background p-2.5"
										>
											<p className="whitespace-pre-line text-sm leading-relaxed">
												{texto}
											</p>
											<div className="mt-2 flex gap-1">
												<Button
													size="sm"
													className="h-7 text-xs"
													onClick={() => usar(texto)}
												>
													Usar
												</Button>
												<Button
													variant="ghost"
													size="icon"
													className="h-7 w-7"
													aria-label="Copiar"
													onClick={() => copiar(texto)}
												>
													<Copy className="h-3.5 w-3.5" />
												</Button>
											</div>
										</li>
									))}
								</ul>
							</Bloco>
						)}

						{/* análise */}
						{analise && (
							<Bloco titulo="Leitura da conversa">
								<div className="flex flex-wrap gap-1.5">
									{analise.humor && (
										<span
											className={cn(
												"rounded-full px-2 py-0.5 text-[11px] font-medium",
												HUMOR[analise.humor] || HUMOR.neutro
											)}
										>
											Cliente {analise.humor}
										</span>
									)}
									{analise.urgencia && (
										<span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
											Urgência {analise.urgencia}
										</span>
									)}
									{analise.riscoDeCancelamento && (
										<span
											className={cn(
												"rounded-full px-2 py-0.5 text-[11px] font-medium",
												RISCO[analise.riscoDeCancelamento] || RISCO.baixo
											)}
										>
											Risco de cancelar: {analise.riscoDeCancelamento}
										</span>
									)}
									{analise.oportunidadeDeVenda === "sim" && (
										<span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
											Oportunidade de venda
										</span>
									)}
								</div>

								{analise.resumoCurto && (
									<p className="mt-2 text-xs leading-relaxed text-muted-foreground">
										{analise.resumoCurto}
									</p>
								)}

								{analise.etiquetasSugeridas?.length > 0 && (
									<p className="mt-2 text-xs">
										<span className="text-muted-foreground">
											Etiquetas sugeridas:{" "}
										</span>
										{analise.etiquetasSugeridas.join(", ")}
									</p>
								)}

								{analise.filaSugerida && (
									<p className="mt-1 text-xs">
										<span className="text-muted-foreground">
											Fila sugerida:{" "}
										</span>
										{analise.filaSugerida}
									</p>
								)}
							</Bloco>
						)}

						{/* resumo */}
						{resumo && (
							<Bloco titulo="Resumo">
								<p className="whitespace-pre-line text-sm leading-relaxed">
									{resumo.resumo}
								</p>

								<dl className="mt-3 space-y-1.5 text-xs">
									{[
										["Motivo do contato", resumo.motivoDoContato],
										["Solução aplicada", resumo.solucaoAplicada],
										["Sentimento", resumo.sentimento],
										["Volta a comprar?", resumo.probabilidadeDeRetorno],
									]
										.filter(([, valor]) => valor)
										.map(([rotulo, valor]) => (
											<div key={rotulo}>
												<dt className="text-muted-foreground">{rotulo}</dt>
												<dd>{valor}</dd>
											</div>
										))}
								</dl>

								{resumo.proximosPassos?.length > 0 && (
									<div className="mt-3">
										<p className="text-xs text-muted-foreground">
											Próximos passos
										</p>
										<ul className="mt-1 list-inside list-disc space-y-0.5 text-xs">
											{resumo.proximosPassos.map((passo, i) => (
												<li key={i}>{passo}</li>
											))}
										</ul>
									</div>
								)}

								<Button
									variant="outline"
									size="sm"
									className="mt-3 h-7 w-full text-xs"
									onClick={() => copiar(resumo.resumo)}
								>
									<Copy className="h-3.5 w-3.5" />
									Copiar resumo
								</Button>
							</Bloco>
						)}

						{/* score */}
						{score && (
							<Bloco titulo="Nota do atendimento">
								<div className="flex items-baseline gap-2">
									<span className="text-3xl font-semibold tabular-nums">
										{score.notaFinal ?? "—"}
									</span>
									<span className="text-sm text-muted-foreground">/ 10</span>
								</div>

								<div className="mt-3 space-y-1.5">
									{[
										["Tempo de resposta", score.tempoDeResposta],
										["Educação", score.educacao],
										["Clareza", score.clareza],
										["Objetividade", score.objetividade],
										["Empatia", score.empatia],
									]
										.filter(([, nota]) => typeof nota === "number")
										.map(([rotulo, nota]) => (
											<div key={rotulo}>
												<div className="flex justify-between text-[11px]">
													<span className="text-muted-foreground">
														{rotulo}
													</span>
													<span className="tabular-nums">{nota}</span>
												</div>
												<div className="mt-0.5 h-1 overflow-hidden rounded-full bg-muted">
													<div
														className={cn(
															"h-full rounded-full",
															nota >= 7
																? "bg-emerald-500"
																: nota >= 5
																? "bg-amber-500"
																: "bg-destructive"
														)}
														style={{ width: `${(nota / 10) * 100}%` }}
													/>
												</div>
											</div>
										))}
								</div>

								{score.pontosAMelhorar?.length > 0 && (
									<div className="mt-3">
										<p className="text-xs text-muted-foreground">
											Pontos a melhorar
										</p>
										<ul className="mt-1 list-inside list-disc space-y-0.5 text-xs">
											{score.pontosAMelhorar.map((ponto, i) => (
												<li key={i}>{ponto}</li>
											))}
										</ul>
									</div>
								)}
							</Bloco>
						)}
					</div>
				</SheetContent>
			</Sheet>
		</>
	);
};

export default AiCopilot;
